import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type {
	CatalogueReference,
	JsonSchema,
	NodeDef,
} from "../../types/flows";
import {
	ExpressionPreview,
	SampleFields,
	useNodeSample,
} from "./ExpressionSample";
import type { DeclaredParam } from "./paramSchema";
import { TriggerParams } from "./TriggerParams";
import { btn, input } from "./ui";

// A form generated from the node's own TypeBox schema, served by
// `GET /flows/nodes`. The schema is the server's validation rule, so rendering
// the form from it is the only way the two cannot disagree about what a valid
// config is — the same reason `planner/fields.ts` feeds `GET /planner/fields`.
//
// Where the schema alone would make a field unusable — a prompt in a one-line
// text box, a free-text `contentType` that has exactly seven legal values — the
// `reference` block from the same response supplies the options, so an invalid
// value cannot be picked in the first place.

/** Enum members: TypeBox renders `t.Union([t.Literal(…)])` as `anyOf` of consts. */
const literalsOf = (schema: JsonSchema): string[] | null => {
	if (!schema.anyOf) return null;
	const values = schema.anyOf
		.map((member) => member.const)
		.filter((value): value is string => typeof value === "string");
	return values.length === schema.anyOf.length && values.length > 0
		? values
		: null;
};

/**
 * `t.Union([t.Number(), t.String()])` — a number, or an expression that produces one.
 *
 * A plain number input makes the expression untypeable; the JSON fallback makes the number look
 * like a mistake. One text box that keeps whichever form the value actually took is the only
 * rendering that serves both.
 */
const isNumberOrExpression = (schema: JsonSchema): boolean =>
	Array.isArray(schema.anyOf) &&
	schema.anyOf.length === 2 &&
	schema.anyOf.some((member) => member.type === "number") &&
	schema.anyOf.some((member) => member.type === "string");

/** `t.Record(t.String(), …)` — an object whose keys the editor invents. */
const isRecord = (schema: JsonSchema): boolean =>
	schema.type === "object" && Boolean(schema.patternProperties);

/**
 * `minMerchants` → `Min merchants`. The schema key is a variable name; the panel is a form, and a
 * person reading it should not have to parse camelCase to find the switch they want.
 */
const ACRONYMS = new Set([
	"seo",
	"id",
	"url",
	"urls",
	"cms",
	"rss",
	"http",
	"llm",
	"ai",
	"utm",
	"json",
	"html",
]);

const humanise = (name: string): string =>
	/^\d+$/.test(name)
		? name
		: name
				.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
				.replace(/[_-]+/g, " ")
				.split(" ")
				.map((word, index) =>
					ACRONYMS.has(word.toLowerCase())
						? word.toUpperCase()
						: index === 0
							? word.charAt(0).toUpperCase() + word.slice(1)
							: word,
				)
				.join(" ");

const Label = ({
	name,
	required,
	hint,
	children,
}: {
	name: string;
	required?: boolean;
	hint?: string;
	children: React.ReactNode;
}) => (
	// biome-ignore lint/a11y/noLabelWithoutControl: the control is the `children` this wraps
	<label className="block space-y-1">
		<span className="flex items-baseline gap-1.5 text-[12px] font-medium text-slate-600">
			{humanise(name)}
			{required && <span className="text-danger">*</span>}
			{hint && (
				<span className="text-[11px] font-normal text-slate-400">{hint}</span>
			)}
		</span>
		{children}
	</label>
);

/**
 * Where a plain schema field has a fixed set of legal values the schema itself
 * does not spell out. Keyed by field name because that is what the server node
 * files use consistently.
 */
function optionsFor(
	name: string,
	reference: CatalogueReference | undefined,
): string[] | null {
	if (!reference) return null;
	if (name === "contentType") return reference.contentTypes;
	if (name === "systemPreset") return reference.systemPresets;
	if (name === "op") return reference.operators.map((o) => o.id);
	return null;
}

const MULTILINE = new Set([
	"prompt",
	"system",
	"schema",
	"instructions",
	"promptHint",
	"caption",
	"message",
	"body",
	"notes",
	"template",
]);

interface FieldProps {
	name: string;
	schema: JsonSchema;
	value: unknown;
	required: boolean;
	reference?: CatalogueReference;
	variables: string[];
	/** Identifies the node whose last run the preview evaluates against. */
	flowId?: number | null;
	nodeId?: string | null;
	onChange: (value: unknown) => void;
}

const VariablePicker = ({
	variables,
	onPick,
}: {
	variables: string[];
	onPick: (token: string) => void;
}) => (
	<select
		className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-500"
		value=""
		onChange={(event) => {
			if (event.target.value) onPick(event.target.value);
			event.currentTarget.value = "";
		}}
	>
		<option value="">Insert a variable…</option>
		{variables.map((v) => (
			<option key={v} value={v}>
				{v}
			</option>
		))}
	</select>
);

/**
 * A field, plus whatever the node said about it.
 *
 * The node definitions are the only description of a node that exists (`registry.ts`), so a
 * `description` on the config schema is the one place a field's meaning can be written down without
 * a second copy to drift. Rendering it here means a new switch explains itself on the day it ships.
 */
const Field = (props: FieldProps) => {
	const { description } = props.schema;
	if (!description) return <FieldControl {...props} />;
	return (
		<div className="space-y-1">
			<FieldControl {...props} />
			<p className="text-[11px] leading-snug text-slate-400">{description}</p>
		</div>
	);
};

const FieldControl = ({
	name,
	schema,
	value,
	required,
	reference,
	variables,
	flowId,
	nodeId,
	onChange,
}: FieldProps) => {
	const literals = literalsOf(schema);
	const fixed = literals ?? optionsFor(name, reference);
	const fallback =
		schema.default === undefined ? undefined : `default: ${schema.default}`;

	if (fixed) {
		return (
			<Label name={name} required={required} hint={fallback}>
				<select
					className={input}
					value={typeof value === "string" ? value : ""}
					onChange={(event) => onChange(event.target.value || undefined)}
				>
					<option value="">
						{schema.default === undefined
							? "— not set —"
							: `— ${schema.default} —`}
					</option>
					{fixed.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			</Label>
		);
	}

	if (isNumberOrExpression(schema)) {
		const text = value === undefined || value === null ? "" : String(value);
		return (
			<Label
				name={name}
				required={required}
				hint={
					fallback
						? `number or expression · ${fallback}`
						: "number or expression"
				}
			>
				<input
					type="text"
					className={input}
					value={text}
					onChange={(event) => {
						const next = event.target.value;
						if (next.trim() === "") {
							onChange(undefined);
							return;
						}
						const asNumber = Number(next);
						onChange(Number.isFinite(asNumber) ? asNumber : next);
					}}
				/>
			</Label>
		);
	}

	if (schema.type === "boolean") {
		// An unset switch is not an off switch: `requireBrand` left out of the config still requires
		// a brand, and a box rendered unchecked said the opposite of what the node would do.
		const on = value === undefined ? schema.default === true : value === true;
		return (
			<label className="flex items-center gap-2 text-[12px] font-medium text-slate-600">
				<input
					type="checkbox"
					checked={on}
					onChange={(event) => onChange(event.target.checked)}
					className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
				/>
				{humanise(name)}
			</label>
		);
	}

	if (schema.type === "number") {
		const bounds =
			schema.minimum !== undefined || schema.maximum !== undefined
				? `${schema.minimum ?? "—"} to ${schema.maximum ?? "—"}`
				: undefined;
		return (
			<Label
				name={name}
				required={required}
				hint={[bounds, fallback].filter(Boolean).join(" · ") || undefined}
			>
				<input
					type="number"
					className={input}
					min={schema.minimum}
					max={schema.maximum}
					value={typeof value === "number" ? value : ""}
					onChange={(event) =>
						onChange(
							event.target.value === ""
								? undefined
								: Number(event.target.value),
						)
					}
				/>
			</Label>
		);
	}

	if (schema.type === "string") {
		if (MULTILINE.has(name)) {
			return (
				<Label name={name} required={required} hint="expressions allowed">
					<div className="space-y-1">
						<textarea
							className={`${input} min-h-32 font-mono text-[12px]`}
							value={typeof value === "string" ? value : ""}
							onChange={(event) => onChange(event.target.value || undefined)}
						/>
						{variables.length > 0 && (
							<VariablePicker
								variables={variables}
								onPick={(token) =>
									onChange(`${typeof value === "string" ? value : ""}${token}`)
								}
							/>
						)}
						<ExpressionPreview
							flowId={flowId ?? null}
							nodeId={nodeId ?? null}
							template={typeof value === "string" ? value : ""}
						/>
					</div>
				</Label>
			);
		}
		// A one-line field holding an expression needs the preview just as much as a prompt does —
		// a meta title built from {{ item.llm.seoTitle }} is exactly the kind of thing that renders
		// empty without saying so. Shown only once there is an expression to evaluate.
		const text = typeof value === "string" ? value : "";
		return (
			<Label name={name} required={required}>
				<div className="space-y-1">
					<input
						type="text"
						className={input}
						value={text}
						onChange={(event) => onChange(event.target.value || undefined)}
					/>
					{text.includes("{{") && (
						<ExpressionPreview
							flowId={flowId ?? null}
							nodeId={nodeId ?? null}
							template={text}
						/>
					)}
				</div>
			</Label>
		);
	}

	if (schema.type === "array" && schema.items) {
		const items = Array.isArray(value) ? value : [];
		const itemSchema = schema.items;
		return (
			<Label name={name} required={required} hint={`${items.length} entries`}>
				<div className="space-y-2 rounded-lg border border-slate-200 p-2">
					{items.map((entry, index) => (
						// Rows have no stable identity — reordering is not offered, so the
						// index is the identity.
						// biome-ignore lint/suspicious/noArrayIndexKey: no stable id exists
						<div key={index} className="flex items-start gap-2">
							<div className="flex-1">
								{itemSchema.type === "object" ? (
									<ObjectFields
										schema={itemSchema}
										value={(entry ?? {}) as Record<string, unknown>}
										reference={reference}
										variables={variables}
										onChange={(next) => {
											const copy = [...items];
											copy[index] = next;
											onChange(copy);
										}}
									/>
								) : (
									<Field
										name={`${index + 1}`}
										schema={itemSchema}
										value={entry}
										required={false}
										reference={reference}
										variables={variables}
										onChange={(next) => {
											const copy = [...items];
											copy[index] = next;
											onChange(copy);
										}}
									/>
								)}
							</div>
							<button
								type="button"
								onClick={() => onChange(items.filter((_, i) => i !== index))}
								className="mt-1 rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-danger"
								aria-label="Remove"
							>
								<Trash2 className="h-3.5 w-3.5" />
							</button>
						</div>
					))}
					<button
						type="button"
						onClick={() =>
							onChange([...items, itemSchema.type === "object" ? {} : ""])
						}
						className="flex items-center gap-1 text-[12px] font-medium text-primary"
					>
						<Plus className="h-3.5 w-3.5" /> Add
					</button>
				</div>
			</Label>
		);
	}

	if (isRecord(schema)) {
		return (
			<Label name={name} required={required} hint="key / expression">
				<KeyValueEditor
					value={(value ?? {}) as Record<string, unknown>}
					onChange={onChange}
					variables={variables}
				/>
			</Label>
		);
	}

	if (schema.type === "object" && schema.properties) {
		return (
			<Label name={name} required={required}>
				<div className="rounded-lg border border-slate-200 p-2">
					<ObjectFields
						schema={schema}
						value={(value ?? {}) as Record<string, unknown>}
						reference={reference}
						variables={variables}
						onChange={onChange}
					/>
				</div>
			</Label>
		);
	}

	// `t.Any()` and anything else: JSON, because guessing would be worse.
	return (
		<Label name={name} required={required} hint="JSON">
			<JsonField value={value} onChange={onChange} />
		</Label>
	);
};

const JsonField = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (value: unknown) => void;
}) => {
	const [text, setText] = useState(() =>
		value === undefined ? "" : JSON.stringify(value),
	);
	const [bad, setBad] = useState(false);

	return (
		<div>
			<input
				type="text"
				className={`${input} font-mono ${bad ? "border-danger" : ""}`}
				value={text}
				onChange={(event) => {
					const next = event.target.value;
					setText(next);
					if (next.trim() === "") {
						setBad(false);
						onChange(undefined);
						return;
					}
					try {
						onChange(JSON.parse(next));
						setBad(false);
					} catch {
						// A half-typed value is not an error worth blocking on; it just
						// does not reach the config until it parses.
						setBad(true);
					}
				}}
			/>
			{bad && (
				<p className="mt-0.5 text-[11px] text-danger">Not valid JSON yet</p>
			)}
		</div>
	);
};

const KeyValueEditor = ({
	value,
	onChange,
	variables,
}: {
	value: Record<string, unknown>;
	onChange: (value: unknown) => void;
	variables: string[];
}) => {
	const [newKey, setNewKey] = useState("");
	const entries = Object.entries(value);

	return (
		<div className="space-y-2 rounded-lg border border-slate-200 p-2">
			{entries.length === 0 && (
				<p className="text-[11px] text-slate-400">Nothing mapped yet.</p>
			)}
			{entries.map(([key, entry]) => (
				<div key={key} className="flex items-start gap-2">
					<span className="mt-2 w-32 shrink-0 truncate font-mono text-[11px] text-slate-600">
						{key}
					</span>
					<textarea
						className={`${input} min-h-9 flex-1 font-mono text-[11px]`}
						value={
							typeof entry === "string" ? entry : JSON.stringify(entry ?? "")
						}
						onChange={(event) =>
							onChange({ ...value, [key]: event.target.value })
						}
					/>
					<button
						type="button"
						onClick={() => {
							const copy = { ...value };
							delete copy[key];
							onChange(copy);
						}}
						className="mt-1.5 rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-danger"
						aria-label={`Remove ${key}`}
					>
						<Trash2 className="h-3.5 w-3.5" />
					</button>
				</div>
			))}
			<div className="flex items-center gap-2">
				<input
					type="text"
					placeholder="new field name"
					className={`${input} flex-1`}
					value={newKey}
					onChange={(event) => setNewKey(event.target.value)}
				/>
				<button
					type="button"
					disabled={!newKey.trim()}
					onClick={() => {
						onChange({ ...value, [newKey.trim()]: "" });
						setNewKey("");
					}}
					className={btn.ghost}
				>
					<Plus className="h-3.5 w-3.5" /> Add
				</button>
			</div>
			{variables.length > 0 && (
				<p className="text-[10px] text-slate-400">
					Values are expressions — {variables.slice(0, 3).join(", ")} and so on.
				</p>
			)}
		</div>
	);
};

const ObjectFields = ({
	schema,
	value,
	reference,
	variables,
	flowId,
	nodeId,
	onChange,
}: {
	schema: JsonSchema;
	value: Record<string, unknown>;
	reference?: CatalogueReference;
	variables: string[];
	flowId?: number | null;
	nodeId?: string | null;
	onChange: (value: Record<string, unknown>) => void;
}) => {
	const required = new Set(schema.required ?? []);
	return (
		<div className="space-y-3">
			{Object.entries(schema.properties ?? {}).map(([name, fieldSchema]) => (
				<Field
					key={name}
					name={name}
					schema={fieldSchema}
					value={value[name]}
					required={required.has(name)}
					reference={reference}
					variables={variables}
					flowId={flowId}
					nodeId={nodeId}
					onChange={(next) => {
						const copy = { ...value };
						if (next === undefined) delete copy[name];
						else copy[name] = next;
						onChange(copy);
					}}
				/>
			))}
		</div>
	);
};

export const ConfigForm = ({
	def,
	config,
	reference,
	upstreamLabels,
	flowId,
	nodeId,
	onChange,
}: {
	def: NodeDef;
	config: Record<string, unknown>;
	reference?: CatalogueReference;
	/** Labels of nodes that run before this one — what `node("…")` may address. */
	upstreamLabels: string[];
	flowId?: number | null;
	nodeId?: string | null;
	onChange: (config: Record<string, unknown>) => void;
}) => {
	const { sample, loading } = useNodeSample(flowId ?? null, nodeId ?? null);
	const [lastPick, setLastPick] = useState<string | null>(null);

	// The real fields this node saw last time, in preference to a generic guess at what an item
	// might carry — which is what this list used to be, and what sent someone looking for
	// `item.out[0]`.
	const variables =
		sample && sample.fields.length > 0
			? [
					...sample.fields.map((field) => `{{ item.${field.path} }}`),
					"{{ items | count }}",
					"{{ params.categoryId }}",
				]
			: [
					"{{ item.name }}",
					"{{ items | count }}",
					"{{ params.categoryId }}",
					"{{ run.id }}",
					...upstreamLabels.map(
						(label) => `{{ node("${label}").items | json }}`,
					),
				];

	if (!def.configSchema.properties) {
		return (
			<p className="text-[12px] text-slate-500">
				This node has nothing to configure.
			</p>
		);
	}

	return (
		<div className="space-y-3">
			<SampleFields
				sample={sample}
				loading={loading}
				onPick={(token) => {
					// Clipboard rather than a guess at which field is being edited: the picker sits
					// above the form and every expression field has its own insert control.
					navigator.clipboard?.writeText(token).catch(() => undefined);
					setLastPick(token);
				}}
			/>
			{lastPick && (
				<p className="text-[10px] text-slate-400">
					Copied <span className="font-mono">{lastPick}</span>
				</p>
			)}

			{def.type === "trigger.manual" ? (
				<TriggerParams
					key={nodeId ?? def.type}
					params={(config.params as DeclaredParam[] | undefined) ?? []}
					onChange={(params) =>
						onChange(
							params.length === 0
								? (({ params: _dropped, ...rest }) => rest)(config)
								: { ...config, params },
						)
					}
				/>
			) : (
				<ObjectFields
					schema={def.configSchema}
					value={config}
					reference={reference}
					variables={variables}
					flowId={flowId}
					nodeId={nodeId}
					onChange={onChange}
				/>
			)}
		</div>
	);
};
